import CloudUploadIcon from "@mui/icons-material/Upload";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../contexts/AuthContext";
import MyModal from "../MyModal";
import { loyaltyService } from "../../services/loyaltyService";

const MAX_FILE_SIZE = 10 * 1024 * 1024;

interface IModalLoyaltyBatch {
  modalChange: (open: boolean) => void;
  onSuccess: () => void;
  onError: (message?: string) => void;
}

export default function ModalLoyaltyBatch({
  modalChange,
  onSuccess,
  onError,
}: IModalLoyaltyBatch) {
  const { t } = useTranslation();
  const { tokenHeaders } = useContext(AuthContext);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [identification, setIdentification] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState("");
  const [saving, setSaving] = useState(false);

  const isValid = identification.trim().length > 0 && file !== null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (!selected.name.toLowerCase().endsWith(".xlsx")) {
      setFileError(t("loyalty.batch.invalidFormat"));
      setFile(null);
      e.target.value = "";
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      setFileError(t("loyalty.batch.fileTooLarge"));
      setFile(null);
      e.target.value = "";
      return;
    }
    setFileError("");
    setFile(selected);
  };

  const handleSubmit = async () => {
    if (!file || !isValid) return;
    setSaving(true);
    try {
      await loyaltyService.uploadBatch(file, identification.trim(), tokenHeaders);
      modalChange(false);
      onSuccess();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ?? t("loyalty.batch.uploadError");
      onError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <MyModal
      title={t("loyalty.batch.title")}
      modalChange={modalChange}
      closeIconSx={{ color: "#001E50", fontSize: "32px" }}
    >
      {/* Identificação */}
      <Box sx={{ mb: "24px" }}>
        <Typography sx={{ fontWeight: 700, mb: "16px", fontSize: "16px", color: "var(--black)" }}>
          {t("loyalty.batch.identification")}*
        </Typography>
        <TextField
          fullWidth
          size="medium"
          label={t("loyalty.batch.groupName")}
          value={identification}
          onChange={(e) => setIdentification(e.target.value)}
          disabled={saving}
        />
      </Box>

      {/* Grupo de participantes */}
      <Box sx={{ mb: "24px" }}>
        <Typography sx={{ fontWeight: 700, mb: 2 }}>
          {t("loyalty.batch.groupParticipants")}*
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t("loyalty.batch.fileType")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("loyalty.batch.maxSize")}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography>{t("loyalty.batch.file")}:</Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <IconButton
              onClick={() => fileInputRef.current?.click()}
              disabled={saving}
              sx={{
                bgcolor: "var(--blue-200)",
                color: "white",
                borderRadius: "12px",
                p: 1,
                "&:hover": { bgcolor: "var(--blue-400)" },
                "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
              }}
            >
              <CloudUploadIcon />
            </IconButton>
            <Typography
              variant="body2"
              sx={{ maxWidth: 220, wordBreak: "break-all", fontSize: "16px", color: "#96A3A8" }}
            >
              {file ? file.name : ""}
            </Typography>
          </Box>
        </Box>

        {fileError && (
          <Typography
            color="error"
            variant="body2"
            sx={{ mt: 1 }}
          >
            {fileError}
          </Typography>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 3,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          *{t("loyalty.batch.requiredFields")}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mt: 3,
        }}
      >
        <Button
          variant="contained"
          onClick={handleSubmit}
          size="medium"
          disabled={!isValid || saving}
          sx={{ borderRadius: "12px", minWidth: 140 }}
        >
          {saving ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("loyalty.batch.register")
          )}
        </Button>
      </Box>
    </MyModal>
  );
}
